/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { StyleSheet, View, TouchableOpacity } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { format, parseISO } from 'date-fns';

import SociollaText from '@components/sociolla/SociollaText';
import SociollaTable from '@components/sociolla/SociollaTable';
import Button from '@components/form/Button';
import ModalLoyaltySetup from '@components/customer/loyalty/ModalLoyaltySetup';
import ModalViewLoyalty from '@components/customer/loyalty/ModalViewLoyalty';
import MoreButton from '@components/navbar/MoreButton';
import NotificationSettingButton from '@components/navbar/NotificationSettingButton';
import { Container } from '@components/screen/Layout';

import Colors from '@global/colors';
import { isWeb } from '@global/screens';
import { buttonPrimary, buttonRounded } from '@/assets/styles/buttonStyles';
import { paramsReseted } from '@/store/params';
import tableApi from '@/hooks/tableApi';
import { getLoyaltyRules, getLoyaltyRulesCount } from '@api/ms-order';
import { hasNoAccess } from '@global/helpers';

let init = true;

function LoyaltySetupScreen(props) {
  const tableRef = useRef();
  const [list, setList] = useState([]);
  const [id, setId] = useState(null);
  const [modalLoyalty, setModalLoyalty] = useState(false);
  const [modalView, setModalView] = useState(false);
  const [isAddMode, setIsAddMode] = useState(true);
  const [selectedRow, setSelectedRow] = useState(null);

  const loyaltySetup = tableApi(getLoyaltyRulesCount, getLoyaltyRules);

  const dispatch = useDispatch();
  const paramsState = useSelector((state) => state.params);

  const hasAccess = hasNoAccess([
    'sales_supervisor',
    'branch_head',
    'finance_supervisor',
    'regional_manager',
    'regional_support',
    'key_account_manager',
    'group_key_account_manager',
    'national_key_account_manager',
    'distributor_manager',
    'vice_president_finance',
    'budget_controller',
    'finance_head',
    'tax_support',
    'billing_supervisor',
    'general_manager_marketing',
    'marketing_admin',
    'marketing_support',
    'regional_distributor_manager',
  ]);
  const header = useMemo(() => {
    const tableHeader = [
      {
        label: 'Rule Name',
        data: 'name',
        width: '40%',
      },
      {
        label: 'Last Update',
        data: 'last_updated',
        width: '20%',
      },
      {
        label: 'Priority',
        data: 'priority',
        styleItem: { alignItems: 'center', padding: 0 },
        width: '15%',
      },
    ];
    if (hasAccess) {
      tableHeader.push(
        {
          label: 'Status',
          data: 'status',
          styleItem: { alignItems: 'center', padding: 0 },
          width: '15%',
        },
        {
          label: 'Action',
          data: '',
          type: 'action',
          action: [
            {
              icon: require('@assets/img/icons/edit-blue.svg'),
              name: 'edit',
            },
          ],
          width: '10%',
        },
      );
    } else {
      tableHeader.push({
        label: 'Status',
        data: 'status',
        styleItem: { alignItems: 'center', padding: 0 },
        width: '25%',
      });
    }
    return tableHeader;
  }, [hasAccess]);

  const renderItem = ({ item, index }) => (
    <TouchableOpacity
      onPress={() => callBackRowPress(item)}
      key={'list-loyalty-' + index}
      style={styles.tableRow}>
      <SociollaText
        type="medium"
        style={{ paddingBottom: 10, fontSize: 16, color: Colors.black1 }}>
        {item.name}
      </SociollaText>
      <SociollaText
        style={{
          position: 'absolute',
          top: 15,
          right: 20,
          fontSize: 10,
          color: Colors.black,
          ...item.statusStyle,
        }}>
        {item.status}
      </SociollaText>
      <View style={{ flexDirection: 'row', width: '100%' }}>
        <View style={{ flex: 1 }}>
          <SociollaText
            style={{ paddingBottom: 5, fontSize: 10, color: Colors.black5 }}>
            Last updated:
          </SociollaText>
          <SociollaText style={{ fontSize: 12, color: Colors.black2 }}>
            {item.last_updated}
          </SociollaText>
        </View>
        <View style={{ flex: 1 }}>
          <SociollaText
            style={{ paddingBottom: 5, fontSize: 10, color: Colors.black5 }}>
            Priority:
          </SociollaText>
          <SociollaText style={{ fontSize: 12, color: Colors.black2 }}>
            {item.priority}
          </SociollaText>
        </View>
      </View>
    </TouchableOpacity>
  );

  const generateStatus = (status) => {
    if (status) {
      return { label: 'Active', color: 'rgba(62, 234, 102, 0.08)' };
    } else {
      return { label: 'Inactive', color: 'rgba(29, 103, 252, 0.08)' };
    }
  };

  const generateData = (data) => {
    return (data || []).map((lp) => {
      const status = generateStatus(lp.is_active);
      const priority = lp.rules.length ? lp.rules[0].priority : '';
      return {
        ...lp,
        last_updated: lp.updated_at
          ? format(parseISO(lp.updated_at), 'dd MMM yyyy')
          : '-',
        priority,
        status: lp.is_active ? 'Active' : 'Inactive',
        statusStyle: {
          backgroundColor: status.color,
          paddingHorizontal: 15,
          paddingVertical: 7,
          borderRadius: 4,
        },
      };
    });
  };

  const onClose = (reload = false) => {
    setIsAddMode(true);
    setId(null);
    setModalLoyalty(false);
    if (reload) {
      loyaltySetup.reset();
      setList([]);
      dispatch(paramsReseted());
    }
  };

  const callBackRowPress = (item) => {
    modalViewLoyalty(item);
  };

  const callBackAction = (data) => {
    if (data.name === 'edit') {
      onEdit(data.data._id);
    }
  };

  const modalViewLoyalty = (item) => {
    if (item) {
      setSelectedRow(item);
      setModalView(true);
    } else {
      setSelectedRow(null);
      setModalView(false);
    }
  };

  const onEdit = (id) => {
    modalViewLoyalty(false);
    setId(id);
    setIsAddMode(false);
    setModalLoyalty(true);
  };

  const callback = (data) => {
    if (data.key === 'create') {
      setIsAddMode(true);
      setModalLoyalty(true);
    }
  };

  const getData = () => {
    loyaltySetup.request(paramsState).then((response) => {
      if (response) {
        setList(generateData(response));
      }
      init = true;
    });
  };

  useEffect(() => {
    if (!init) {
      getData();
    }
  }, [paramsState]);

  useEffect(() => {
    let mount = false;
    props.navigation.setOptions({
      headerRight: () => {
        return isWeb ? (
          <NotificationSettingButton {...props} />
        ) : hasAccess ? (
          <MoreButton
            menu={[{ type: 'props', name: 'Create Rule', key: 'create' }]}
            callback={(value) => callback(value)}
          />
        ) : (
          false
        );
      },
    });
    if (!mount) {
      getData();
    }
    props.navigation.addListener('blur', () => {
      init = true;
    });
    return () => {
      mount = true;
      props.navigation.removeListener('blur');
    };
  }, [props]);

  return (
    <Container>
      {isWeb && (
        <SociollaText
          type="medium"
          color="#171D29"
          style={{ marginBottom: 40, fontSize: 24 }}>
          Loyalty Point Setup
        </SociollaText>
      )}
      {isWeb && (
        <SociollaTable
          table={header}
          ref={tableRef}
          datas={list}
          showPagination
          count={loyaltySetup.count}
          loading={loyaltySetup.loading}
          customAction={
            hasAccess ? (
              <Button
                buttonStyle={{ ...buttonPrimary, ...buttonRounded }}
                title={
                  <SociollaText
                    type="medium"
                    style={{
                      fontSize: 14,
                      color: Colors.white,
                      paddingHorizontal: 10,
                    }}>
                    Create Rule
                  </SociollaText>
                }
                onPress={() => setModalLoyalty(true)}
              />
            ) : (
              false
            )
          }
          callBackAction={(act) => callBackAction(act)}
        />
      )}
      {!isWeb && (
        <SociollaTable
          datas={list}
          itemComponent={renderItem}
          headerComponent={<></>}
          loading={loyaltySetup.loading}
          scrollItem={true}
          hideSearch={true}
          loadingLoadMore={loyaltySetup.loadingLoadMore}
        />
      )}
      {modalLoyalty && (
        <ModalLoyaltySetup
          show={modalLoyalty}
          onClose={(reload) => onClose(reload)}
          isAddMode={isAddMode}
          id={id}
        />
      )}
      {modalView && (
        <ModalViewLoyalty
          show={modalView}
          data={selectedRow}
          onClose={() => modalViewLoyalty(false)}
          onEdit={(val) => onEdit(val)}
        />
      )}
    </Container>
  );
}

const styles = StyleSheet.create({
  tableRow: {
    justifyContent: 'center',
    alignItems: 'flex-start',
    flexDirection: 'column',
    minHeight: 93,
    width: '100%',
    flex: 1,
    borderBottomColor: Colors.grey9,
    borderBottomWidth: 1,
    paddingHorizontal: 20,
  },
});

export default LoyaltySetupScreen;
